import { ApiOkResponse, ApiResponse } from 'apisauce';
import { RobocloudError } from '~/types/common';
import { ApiError, DefaultApiError } from './error';

type ApiOkResponseWithDefinedData<T> = Omit<ApiOkResponse<T>, 'data'> & {
  data: Exclude<ApiOkResponse<T>['data'], undefined>;
};

/**
 * Simplifies checking and throwing exception in queries for failed requests
 */
export const validateApiResponse = <T, U = T>(
  response: ApiResponse<T, U>,
): ApiOkResponseWithDefinedData<T> => {
  if (!response.ok) {
    const errorData = (response.data as RobocloudError)?.error;
    throw new ApiError(errorData?.message || response.problem, {
      status: response.status,
      code: errorData?.code,
      subCode: errorData?.subCode,
      method: response.config?.method,
    });
  }
  return response as ApiOkResponseWithDefinedData<T>;
};

interface ErrorPayloadOptions {
  type?: string;
  showSnackbar?: boolean;
  displayOriginalError?: boolean;
  userMessageVariant?: 'default' | 'danger' | 'success';
  [key: string]: unknown;
}

/**
 * Formats received error to be compatible with user message reducer expected payload.
 * Expects:
 * - ApiError when response fails validateApiResponse check
 * - Error or subset of this when unhandled logic exception has been thrown
 * - optional type to be used in snackbar message
 */
export const formatErrorPayload = (
  { message, ...error }: Error | ApiError,
  overrides: ErrorPayloadOptions = {},
) => {
  const { type, displayOriginalError, ...mergedError } = {
    error: message,
    subCode: undefined,
    ...error,
    ...overrides,
  };

  return {
    ...mergedError,
    type: type ?? DefaultApiError.UNKNOWN,
    displayOriginalError: displayOriginalError ?? false,
  };
};
